import { createApiInstance } from "./api";
import { useApiStore } from "@/stores/apiStore";
import axios, { AxiosError } from "axios";

// ⛳ Mocks en desarrollo

/** Métodos HTTP soportados por el sistema de mocks. */
type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

/**
 * Función que genera una respuesta mock.
 * Debe devolver el payload que esperaría la capa de datos (puede ser síncrono o asíncrono).
 *
 * @param body - (Opcional) Cuerpo de la petición (por ejemplo, payload de un POST).
 * @returns Cualquier valor compatible con la respuesta esperada o una Promesa de dicho valor.
 */
type MockHandler = (payload?: any) => any | Promise<any>;

/** Registro interno de handlers de mock indexado por `${method}:${url}`. */
const MOCK_HANDLERS = new Map<string, MockHandler>();

/**
 * Registra un handler de mock para un endpoint concreto.
 *
 * La clave de registro es `${method}:${url}` y **debe coincidir exactamente**
 * con el `endpoint` que se pasa a `fetchGetData` / `fetchPostData`.
 *
 * @param method - Método HTTP a interceptar (`"GET"` | `"POST"`).
 * @param url - Endpoint tal cual se usa en la llamada (path o URL absoluta).
 * @param handler - Función que devuelve la respuesta mock (puede ser async).
 *
 * @example
 * registerMock("GET", endpoints.workingHoursData, () => mockWorkingHoursData);
 * @example
 * registerMock("POST", endpoints.workingHoursData, (body) => ({ resultado: 0, mensaje: "OK" }));
 */
export function registerMock(
  method: HttpMethod,
  url: string,
  handler: MockHandler,
) {
  MOCK_HANDLERS.set(`${method}:${url}`, handler);
}

/**
 * Indica si el sistema de mocks está habilitado.
 *
 * Se considera activo solo cuando:
 *  - `import.meta.env.DEV` es `true` (modo desarrollo), y
 *  - `VITE_ENABLE_MOCKS === "true"`.
 *
 * Nota: este flag es **global**; los flags por módulo se comprueban en sus respectivos
 * ficheros `setup*Mocks.ts` antes de registrar los handlers.
 *
 * @returns `true` si los mocks globales están habilitados; de lo contrario `false`.
 */
function shouldUseMocks() {
  return import.meta.env.DEV && import.meta.env.VITE_ENABLE_MOCKS === "true";
}

/**
 * Intenta servir una respuesta de mock para un método + URL.
 *
 * Si los mocks están habilitados y existe un handler registrado para `${method}:${url}`,
 * ejecuta el handler y devuelve su resultado. En caso contrario, devuelve `undefined`
 * para que la capa HTTP continúe con la petición real.
 *
 * Aplica una **latencia artificial** (~200 ms) para simular condiciones de red.
 *
 * @param method - Método HTTP de la solicitud (`"GET"` | `"POST"`).
 * @param url - Endpoint exactamente igual al usado en `fetchGetData`/`fetchPostData`.
 * @param body - (Opcional) Cuerpo de la petición (se pasa al handler por si necesita decidir en base a él).
 * @returns El resultado del handler de mock o `undefined` si no procede mockear.
 *
 * @example
 * const mocked = await maybeServeMock("GET", endpoints.users);
 * if (mocked !== undefined) return mocked;
 */
async function maybeServeMock(method: HttpMethod, url: string, body?: any) {
  if (!shouldUseMocks()) return undefined;
  const handler = MOCK_HANDLERS.get(`${method}:${url}`);
  if (!handler) return undefined;

  // Simular latencia
  await new Promise((r) => setTimeout(r, 200));
  return handler(body);
}

/**
 * Realiza una solicitud GET a un endpoint especificado.
 *
 * @param endpoint - El endpoint al que se enviará la solicitud.
 * @param token - El token de autenticación que se incluirá en los encabezados.
 * @param params - Un objeto con los parámetros que se enviarán en la URL.
 *
 * @returns Una Promesa que se resuelve con los datos de la respuesta o se rechaza con un mensaje de error.
 *
 * @throws Lanza un mensaje de error si la solicitud falla o encuentra un problema.
 */
export async function fetchGetData<T>(
  endpoint: string,
  token: string,
  params?: Record<string, any>,
): Promise<T> {
  const mocked = await maybeServeMock("GET", endpoint);
  if (mocked !== undefined) return mocked as T;

  const lang = localStorage.getItem("language") || "es";
  const apiStore = useApiStore();
  // instancia de Axios con baseURL personalizada que comprueba si hay conexión a internet
  const api = createApiInstance(apiStore.apiUrl);

  const config = {
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
      "Accept-Language": lang,
    },
    params,
  };

  try {
    const response = await api.get(endpoint, config);
    return response.data as T;
  } catch (error) {
    const formatted = handleAxiosError(error);
    if (typeof formatted === "string") throw formatted;
    throw error;
  }
}

/**
 * Realiza una solicitud GET a un endpoint especificado y devuelve los datos como un Blob.
 *
 * @param endpoint - El endpoint al que se enviará la solicitud.
 * @param token - El token de autenticación que se incluirá en los encabezados.
 * @param params - Un objeto con los parámetros que se enviarán en la URL.
 *
 * @returns Una Promesa que se resuelve con los datos de la respuesta como un Blob o se rechaza con un mensaje de error.
 *
 * @throws Lanza un mensaje de error si la solicitud falla o encuentra un problema.
 */
export async function fetchBlobData(
  endpoint: string,
  token: string,
  params?: Record<string, any>,
): Promise<Blob> {
  const mocked = await maybeServeMock("GET", endpoint);
  if (mocked !== undefined) return mocked as Blob;
  const lang = localStorage.getItem("language") || "es";
  const apiStore = useApiStore();
  const api = createApiInstance(apiStore.apiUrl);

  const config = {
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
      "Accept-Language": lang,
    },
    params,
    responseType: "blob" as const,
  };

  try {
    const response = await api.get(endpoint, config);
    return response.data;
  } catch (error) {
    const formatted = handleAxiosError(error);
    if (typeof formatted === "string") throw formatted;
    throw error;
  }
}

/**
 * Realiza una solicitud POST y obtiene datos de un endpoint especificado utilizando el token y los datos proporcionados.
 *
 * @param endpoint - El endpoint de la API al que se enviará la solicitud.
 * @param token - El token de autenticación que se incluirá en los encabezados de la solicitud.
 * @param data - Los datos que se enviarán en el cuerpo de la solicitud.
 * @param timeoutOverride - Opcional, tiempo de espera a la respuesta de la API (por defecto 10 seg)
 *
 * @returns Una Promesa que se resuelve con los datos de la respuesta o se rechaza con un mensaje de error.
 *
 * @throws Lanza un mensaje de error si la solicitud falla o encuentra un problema.
 */
export async function fetchPostData<T>(
  endpoint: string,
  token: string,
  data: any,
  timeoutOverride?: number,
): Promise<T> {
  const mocked = await maybeServeMock("POST", endpoint, data);
  if (mocked !== undefined) return mocked as T;
  const lang = localStorage.getItem("language") || "es";
  const apiStore = useApiStore();
  // instancia de Axios con baseURL personalizada que comprueba si hay conexión a internet
  const api = createApiInstance(apiStore.apiUrl, timeoutOverride);

  const config = {
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
      "Accept-Language": lang,
    },
  };

  try {
    const response = await api.post(endpoint, data, config);
    return response.data as T;
  } catch (error) {
    const formatted = handleAxiosError(error);
    if (typeof formatted === "string") throw formatted;
    throw error;
  }
}

/**
 * Realiza una solicitud PUT y obtiene datos de un endpoint especificado utilizando el token y los datos proporcionados.
 *
 * @param endpoint - El endpoint de la API al que se enviará la solicitud.
 * @param token - El token de autenticación (Bearer).
 * @param data - Datos a enviar en el cuerpo (si el endpoint no necesita body, pasa {}).
 * @param timeoutOverride - Opcional, tiempo de espera (por defecto el del api instance).
 */
export async function fetchPutData<T>(
  endpoint: string,
  token: string,
  data: unknown = {},
  timeoutOverride?: number,
): Promise<T> {
  const mocked = await maybeServeMock("PUT", endpoint, data);
  if (mocked !== undefined) return mocked as T;

  const lang = localStorage.getItem("language") || "es";
  const apiStore = useApiStore();
  const api = createApiInstance(apiStore.apiUrl, timeoutOverride);

  const config = {
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
      "Accept-Language": lang,
    },
  };

  try {
    const response = await api.put(endpoint, data, config);
    return response.data as T;
  } catch (error) {
    const formatted = handleAxiosError(error);
    if (typeof formatted === "string") throw formatted;
    throw error;
  }
}

/**
 * Realiza una solicitud DELETE y obtiene datos de un endpoint especificado utilizando el token y los datos proporcionados.
 *
 * @param endpoint - El endpoint de la API al que se enviará la solicitud.
 * @param token - El token de autenticación (Bearer).
 * @param params - Datos a enviar como parámetros de consulta (si el endpoint no necesita params, pasa {}).
 * @param timeoutOverride - Opcional, tiempo de espera (por defecto el del api instance).
 */
export async function fetchDeleteData<T>(
  endpoint: string,
  token: string,
  params?: Record<string, any>,
  timeoutOverride?: number,
): Promise<T> {
  const mocked = await maybeServeMock("DELETE", endpoint, params);
  if (mocked !== undefined) return mocked as T;

  const lang = localStorage.getItem("language") || "es";
  const apiStore = useApiStore();
  const api = createApiInstance(apiStore.apiUrl, timeoutOverride);

  const config = {
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
      "Accept-Language": lang,
    },
    params,
  };

  try {
    const response = await api.delete(endpoint, config);
    return response.data as T;
  } catch (error) {
    const formatted = handleAxiosError(error);
    if (typeof formatted === "string") throw formatted;
    throw error;
  }
}

/**
 * Gestiona y formatea los errores provenientes de solicitudes HTTP realizadas con Axios.
 *
 * @param error - El objeto de error capturado de una solicitud de Axios. Puede ser de tipo AxiosError o cualquier otro error.
 * @returns Un mensaje en formato de cadena que describe el error. Puede ser:
 *          - El mensaje de error de la respuesta del servidor.
 *          - Un mensaje de error formateado con el código de estado HTTP.
 *          - Un mensaje indicando que no se recibió respuesta.
 *          - El mensaje de la propiedad `message` si es un objeto de tipo Error estándar.
 *          - Un mensaje genérico de "error inesperado" para cualquier otro tipo de error.
 */
export function handleAxiosError(error: unknown): string | AxiosError {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      // Error con respuesta del servidor (ej: 401, 400...)
      const data = error.response.data;
      if (typeof data === "string" && data.trim()) return data; // 👈 usa el texto plano del backend
      return (
        data?.message || `Código ${error.response.status}: Error desconocido.`
      );
    } else if (error.code === "ECONNABORTED") {
      // ⏱️ Timeout
      return "⏱️ Tiempo de espera agotado. El servidor tardó demasiado en responder.";
    } else if (error.request) {
      // ❌ Error de red (sin respuesta del servidor)
      // 👉 Lo dejamos pasar para que el interceptor lo capture
      return error;
    }
  }

  return error instanceof Error ? error.message : "Error inesperado.";
}
